// Forms and intake (spec §16, §31, §32, §45; ADR 0011). The vocabulary,
// the shape of a form definition and the one validator every submission
// goes through, shared by Platform Core (which is authoritative), the web
// Worker (which renders public forms and checks uploads before storing
// them) and the staff UI.
//
// A published version is frozen: submissions name the version they were
// made against and are always read back against exactly that version.

import { z } from 'zod'
import { smsDisclosureIssues } from './comms-sms'

export const FORM_FIELD_TYPES = [
  'short_text',
  'long_text',
  'email',
  'phone',
  'number',
  'date',
  'single_choice',
  'multiple_choice',
  'checkbox',
  'file',
  'section',
] as const
export type FormFieldType = (typeof FORM_FIELD_TYPES)[number]

export const FORM_FIELD_TYPE_LABELS: Record<FormFieldType, string> = {
  short_text: 'Short text',
  long_text: 'Long text',
  email: 'Email',
  phone: 'Phone',
  number: 'Number',
  date: 'Date',
  single_choice: 'Single choice',
  multiple_choice: 'Multiple choice',
  checkbox: 'Checkbox or consent',
  file: 'File upload',
  section: 'Section heading or help text',
}

// What a field means to Hub, beyond its answer. The contact fields are
// evidence for finding the submitter's BRE account (never an identity key:
// spec §4 rule 7); the organization name becomes a proposal for review,
// never a match.
export const FORM_FIELD_ROLES = ['contact_name', 'contact_email', 'contact_phone', 'organization_name'] as const
export type FormFieldRole = (typeof FORM_FIELD_ROLES)[number]
export const FORM_FIELD_ROLE_LABELS: Record<FormFieldRole, string> = {
  contact_name: 'Submitter’s name',
  contact_email: 'Submitter’s email',
  contact_phone: 'Submitter’s phone',
  organization_name: 'Organization name',
}
const ROLE_TYPES: Record<FormFieldRole, FormFieldType[]> = {
  contact_name: ['short_text'],
  contact_email: ['email'],
  contact_phone: ['phone'],
  organization_name: ['short_text'],
}

// A checkbox is an acknowledgement ("I have read the vendor rules") or
// explicit marketing consent. Marketing consent is never required and never
// pre-checked; nothing else on a form implies it (spec §45). Text-message
// marketing has its own box (ADR 0036): an email box never grants texts, and
// the SMS box's label is the TCPA disclosure the person agrees to.
export const FORM_CHECKBOX_PURPOSES = ['acknowledgement', 'marketing_consent', 'sms_marketing_consent'] as const
export type FormCheckboxPurpose = (typeof FORM_CHECKBOX_PURPOSES)[number]

// What the public may upload. HTML, SVG, archives and anything executable
// are refused; the web Worker also checks each file's first bytes against
// the type it claims.
export const FORM_UPLOAD_TYPES = {
  'application/pdf': { label: 'PDF', extensions: ['pdf'] },
  'image/png': { label: 'PNG image', extensions: ['png'] },
  'image/jpeg': { label: 'JPEG image', extensions: ['jpg', 'jpeg'] },
  'image/webp': { label: 'WebP image', extensions: ['webp'] },
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': { label: 'Word document', extensions: ['docx'] },
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': { label: 'Excel workbook', extensions: ['xlsx'] },
} as const
export type FormUploadType = keyof typeof FORM_UPLOAD_TYPES
export const FORM_UPLOAD_TYPE_KEYS = Object.keys(FORM_UPLOAD_TYPES) as FormUploadType[]
export const isFormUploadType = (value: string): value is FormUploadType => value in FORM_UPLOAD_TYPES

export const FORM_UPLOAD_MAX_MB = 10
export const FORM_UPLOAD_MAX_FILES = 5
// The whole public request, files included.
export const FORM_REQUEST_MAX_BYTES = 25 * 1024 * 1024
export const FORM_MAX_FIELDS = 60
export const FORM_TEXT_MAX = 5000

// Where files wait until a reviewer accepts them into the document catalog.
export const FORM_QUARANTINE_PREFIX = 'forms-quarantine/'

const KEY = z.string().regex(/^[a-z][a-z0-9_]{0,39}$/, 'Keys are lowercase letters, digits and underscores, starting with a letter')

export const FormFieldSchema = z.object({
  key: KEY,
  type: z.enum(FORM_FIELD_TYPES),
  label: z.string().trim().min(1).max(300),
  help: z.string().trim().max(2000).nullable().optional(),
  required: z.boolean().default(false),
  // Hidden in lists and exports unless the reviewer holds
  // documents.confidential.
  sensitive: z.boolean().default(false),
  role: z.enum(FORM_FIELD_ROLES).nullable().optional(),
  options: z.array(z.string().trim().min(1).max(200)).max(50).optional(),
  minLength: z.number().int().min(0).max(FORM_TEXT_MAX).nullable().optional(),
  maxLength: z.number().int().min(1).max(FORM_TEXT_MAX).nullable().optional(),
  min: z.number().finite().nullable().optional(),
  max: z.number().finite().nullable().optional(),
  wholeNumber: z.boolean().optional(),
  purpose: z.enum(FORM_CHECKBOX_PURPOSES).optional(),
  accept: z.array(z.enum(FORM_UPLOAD_TYPE_KEYS as [FormUploadType, ...FormUploadType[]])).min(1).optional(),
  maxFiles: z.number().int().min(1).max(FORM_UPLOAD_MAX_FILES).optional(),
  maxSizeMb: z.number().int().min(1).max(FORM_UPLOAD_MAX_MB).optional(),
})
export type FormField = z.infer<typeof FormFieldSchema>

export const FormDefinitionSchema = z
  .object({
    title: z.string().trim().min(1).max(200),
    intro: z.string().trim().max(4000).nullable().optional(),
    fields: z.array(FormFieldSchema).min(1).max(FORM_MAX_FIELDS),
    confirmationMessage: z.string().trim().min(1).max(2000),
    submitLabel: z.string().trim().min(1).max(40).nullable().optional(),
  })
  .superRefine((def, ctx) => {
    const keys = new Set<string>()
    const roles = new Set<string>()
    def.fields.forEach((field, index) => {
      const at = (message: string) => ctx.addIssue({ code: 'custom', path: ['fields', index], message: `${field.label}: ${message}` })
      if (keys.has(field.key)) at(`the key “${field.key}” is used twice`)
      keys.add(field.key)
      if (field.role) {
        if (roles.has(field.role)) at(`only one field can be the ${FORM_FIELD_ROLE_LABELS[field.role].toLowerCase()}`)
        roles.add(field.role)
        if (!ROLE_TYPES[field.role].includes(field.type)) at(`a ${FORM_FIELD_TYPE_LABELS[field.type].toLowerCase()} field can’t be the ${FORM_FIELD_ROLE_LABELS[field.role].toLowerCase()}`)
      }
      if (field.type === 'section' && (field.required || field.sensitive)) at('a section holds no answer, so it can’t be required or sensitive')
      if ((field.type === 'single_choice' || field.type === 'multiple_choice') && (!field.options || field.options.length === 0)) at('add at least one choice')
      if (field.options && new Set(field.options).size !== field.options.length) at('each choice must be different')
      if (field.type === 'checkbox' && field.purpose === 'marketing_consent' && field.required) at('marketing consent can never be required')
      if (field.type === 'checkbox' && field.purpose === 'sms_marketing_consent') {
        if (field.required) at('text-message consent can never be required')
        const missing = smsDisclosureIssues(field.label)
        if (missing.length) at(`the text-message consent wording must ${missing.join(', ')}`)
        if (!def.fields.some((f) => f.role === 'contact_phone')) at('text-message consent needs a phone field marked as the submitter’s phone')
      }
      if (field.minLength != null && field.maxLength != null && field.minLength > field.maxLength) at('the minimum length is more than the maximum')
      if (field.min != null && field.max != null && field.min > field.max) at('the minimum is more than the maximum')
    })
  })
export type FormDefinition = z.infer<typeof FormDefinitionSchema>

// ------------------------------------------------------------ validation

export type FormAnswer = string | string[] | number | boolean | null
export type FormAnswers = Record<string, FormAnswer>
// A file as the web Worker received it, before or after storing it.
export type FormFileInput = { fieldKey: string; filename: string; contentType: string; sizeBytes: number }

const EMAIL = /^[^\s@<>"]{1,64}@[^\s@<>"]+\.[^\s@<>"]{2,}$/
const DATE = /^\d{4}-\d{2}-\d{2}$/

// Control characters out (tabs and newlines stay in long text); what
// remains is stored and shown as text, never as markup.
const clean = (value: string, multiline: boolean) =>
  value.replace(multiline ? /[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f]/g : /[\u0000-\u001f\u007f]/g, multiline ? '' : ' ').replace(/\r\n?/g, '\n').trim()

const asText = (raw: unknown): string | null => {
  if (typeof raw === 'string') return raw
  if (Array.isArray(raw) && typeof raw[0] === 'string') return raw[0]
  return null
}

export function phoneDigits(value: string): string {
  const digits = value.replace(/\D/g, '')
  return digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits
}

// The one check every submission passes, against the exact version it was
// made on. Keys the version doesn't have are dropped, never stored.
export function validateSubmission(
  def: FormDefinition,
  raw: Record<string, unknown>,
  files: FormFileInput[] = [],
): { ok: true; answers: FormAnswers } | { ok: false; errors: Record<string, string> } {
  const answers: FormAnswers = {}
  const errors: Record<string, string> = {}
  for (const field of def.fields) {
    if (field.type === 'section') continue
    const value = raw[field.key]
    const missing = () => {
      if (field.required) errors[field.key] = 'This is required.'
      answers[field.key] = null
    }
    switch (field.type) {
      case 'short_text':
      case 'long_text':
      case 'email':
      case 'phone':
      case 'date':
      case 'number': {
        const multiline = field.type === 'long_text'
        const text = clean(asText(value) ?? '', multiline)
        if (!text) {
          missing()
          break
        }
        const max = Math.min(field.maxLength ?? (multiline ? FORM_TEXT_MAX : 300), FORM_TEXT_MAX)
        if (text.length > max) {
          errors[field.key] = `Use at most ${max} characters.`
          break
        }
        if (field.minLength && text.length < field.minLength) {
          errors[field.key] = `Use at least ${field.minLength} characters.`
          break
        }
        if (field.type === 'email' && (!EMAIL.test(text) || text.length > 320)) {
          errors[field.key] = 'Enter an email address like name@example.com.'
          break
        }
        if (field.type === 'phone') {
          const digits = phoneDigits(text)
          if (!/^[\d\s()+.-]+$/.test(text) || digits.length < 10 || digits.length > 15) {
            errors[field.key] = 'Enter a phone number with area code.'
            break
          }
        }
        if (field.type === 'date') {
          const time = Date.parse(`${text}T00:00:00Z`)
          if (!DATE.test(text) || Number.isNaN(time) || new Date(time).toISOString().slice(0, 10) !== text) {
            errors[field.key] = 'Enter a date as YYYY-MM-DD.'
            break
          }
        }
        if (field.type === 'number') {
          const number = Number(text.replace(/,/g, ''))
          if (!/^-?[\d,]*\.?\d+$/.test(text) || !Number.isFinite(number)) {
            errors[field.key] = 'Enter a number.'
            break
          }
          if (field.wholeNumber && !Number.isInteger(number)) {
            errors[field.key] = 'Enter a whole number.'
            break
          }
          if (field.min != null && number < field.min) {
            errors[field.key] = `Enter ${field.min} or more.`
            break
          }
          if (field.max != null && number > field.max) {
            errors[field.key] = `Enter ${field.max} or less.`
            break
          }
          answers[field.key] = number
          break
        }
        answers[field.key] = text
        break
      }
      case 'single_choice': {
        const text = asText(value)?.trim() ?? ''
        if (!text) {
          missing()
          break
        }
        if (!field.options?.includes(text)) {
          errors[field.key] = 'Choose one of the options.'
          break
        }
        answers[field.key] = text
        break
      }
      case 'multiple_choice': {
        const list = (Array.isArray(value) ? value : typeof value === 'string' && value ? [value] : []).filter((v): v is string => typeof v === 'string').map((v) => v.trim())
        if (list.length === 0) {
          missing()
          if (!field.required) answers[field.key] = []
          break
        }
        if (list.some((v) => !field.options?.includes(v)) || new Set(list).size !== list.length) {
          errors[field.key] = 'Choose from the options.'
          break
        }
        // In the version's order, whatever order they arrived in.
        answers[field.key] = field.options!.filter((option) => list.includes(option))
        break
      }
      case 'checkbox': {
        const text = asText(value)
        const checked = value === true || text === 'on' || text === 'yes' || text === 'true'
        if (field.required && !checked) errors[field.key] = 'Check this box to continue.'
        answers[field.key] = checked
        break
      }
      case 'file': {
        const mine = files.filter((file) => file.fieldKey === field.key)
        if (mine.length === 0) {
          if (field.required) errors[field.key] = 'Attach a file.'
          answers[field.key] = []
          break
        }
        const maxFiles = field.maxFiles ?? 1
        const maxBytes = (field.maxSizeMb ?? FORM_UPLOAD_MAX_MB) * 1024 * 1024
        const accept = field.accept ?? FORM_UPLOAD_TYPE_KEYS
        if (mine.length > maxFiles) errors[field.key] = maxFiles === 1 ? 'Attach one file.' : `Attach at most ${maxFiles} files.`
        else if (mine.some((file) => !(accept as string[]).includes(file.contentType))) errors[field.key] = `Attach ${accept.map((t) => FORM_UPLOAD_TYPES[t].label).join(', ')} files only.`
        else if (mine.some((file) => file.sizeBytes <= 0 || file.sizeBytes > maxBytes)) errors[field.key] = `Each file must be under ${field.maxSizeMb ?? FORM_UPLOAD_MAX_MB} MB.`
        answers[field.key] = mine.map((file) => file.filename)
        break
      }
    }
  }
  // Files for fields the version doesn't have (or that aren't file fields)
  // are refused rather than silently kept.
  const fileKeys = new Set(def.fields.filter((f) => f.type === 'file').map((f) => f.key))
  if (files.some((file) => !fileKeys.has(file.fieldKey))) errors._form = 'A file was attached to something that isn’t a file question.'
  return Object.keys(errors).length > 0 ? { ok: false, errors } : { ok: true, answers }
}

// The submitter's contact and organization, as typed, from the fields the
// version marks with those roles. Evidence only.
export function submissionContact(def: FormDefinition, answers: FormAnswers) {
  const byRole = (role: FormFieldRole) => {
    const field = def.fields.find((f) => f.role === role)
    const value = field ? answers[field.key] : null
    return typeof value === 'string' && value ? value : null
  }
  return { name: byRole('contact_name'), email: byRole('contact_email'), phone: byRole('contact_phone'), organizationName: byRole('organization_name') }
}

// Explicit marketing consent as given on this submission: one record per
// marketing-consent checkbox, checked or not, with the statement's exact
// wording. An unchecked box is recorded as not given.
export function marketingConsents(def: FormDefinition, answers: FormAnswers) {
  return def.fields
    .filter((f) => f.type === 'checkbox' && (f.purpose === 'marketing_consent' || f.purpose === 'sms_marketing_consent'))
    .map((f) => ({ fieldKey: f.key, statement: f.label, granted: answers[f.key] === true, channel: f.purpose === 'sms_marketing_consent' ? ('sms' as const) : ('email' as const) }))
}

// ---------------------------------------------------------------- routing

export const FORM_CASE_QUEUES = ['box_office', 'rentals', 'sponsorship', 'rewards', 'finance', 'technical', 'general'] as const
export const FORM_PRIORITIES = ['low', 'normal', 'high', 'urgent'] as const
export const FORM_PROPOSAL_ROLES = ['vendor', 'sponsor', 'renter', 'promoter', 'funder', 'group_buyer'] as const
export type FormProposalRole = (typeof FORM_PROPOSAL_ROLES)[number]

const PERSON_ID = z.string().min(1).max(100)

// What happens to each submission, set per form. Every action is recorded
// on the submission with its outcome; one that needs a person (a checklist
// on the submitter) waits until one is linked.
export const FormRoutingSchema = z.object({
  supportCase: z.object({ queue: z.enum(FORM_CASE_QUEUES), priority: z.enum(FORM_PRIORITIES).default('normal') }).nullable().optional(),
  task: z
    .object({
      assigneePersonId: PERSON_ID.nullable().optional(),
      priority: z.enum(FORM_PRIORITIES).default('normal'),
      dueInDays: z.number().int().min(0).max(90).nullable().optional(),
    })
    .nullable()
    .optional(),
  checklist: z
    .object({ templateId: z.string().min(1).max(100), subject: z.enum(['person', 'organization']), ownerPersonId: PERSON_ID.nullable().optional() })
    .nullable()
    .optional(),
  notify: z.object({ personIds: z.array(PERSON_ID).min(1).max(10), email: z.boolean().default(false) }).nullable().optional(),
  organizationProposal: z.object({ role: z.enum(FORM_PROPOSAL_ROLES) }).nullable().optional(),
  // A volunteer application (ADR 0016) for the linked person, optionally
  // for one opportunity. Waits until the submission is linked to a person.
  volunteerApplication: z.object({ opportunityId: z.string().min(1).max(100).nullable().optional() }).nullable().optional(),
  // Starts a published workflow (ADR 0020): on the submission itself, or,
  // for a workflow about a person, once the submission is linked to one.
  startWorkflow: z.object({ templateId: z.string().min(1).max(100) }).nullable().optional(),
  // A board prospect for the Nominating Committee (ADR 0060): one per
  // submission, linked to the person when the submission is (or later).
  boardProspect: z.object({}).nullable().optional(),
})
export type FormRouting = z.infer<typeof FormRoutingSchema>

// ---------------------------------------------------------------- statuses

export const FORM_STATUSES = ['draft', 'published', 'closed', 'archived'] as const
export type FormStatus = (typeof FORM_STATUSES)[number]
export const SUBMISSION_STATUSES = ['new', 'in_review', 'accepted', 'declined', 'spam'] as const
export type SubmissionStatus = (typeof SUBMISSION_STATUSES)[number]
export const SUBMISSION_STATUS_LABELS: Record<SubmissionStatus, string> = { new: 'New', in_review: 'In review', accepted: 'Accepted', declined: 'Declined', spam: 'Spam' }

// How the submission came to be linked to a Hub person, or why it isn't.
//   linked         BRE Auth answered with a proven account or a placeholder;
//                  the person is the one linked to exactly that subject
//   manual         a reviewer linked it
//   unverified     Auth found an account that holds the email but never
//                  proved it: not linked, left for a reviewer
//   no_contact     no email or phone to ask Auth about
//   not_connected  person linking is off in this Hub
//   failed         Auth didn't answer usefully; a reviewer can link it
//   skipped_spam   flagged as spam, so Auth was never asked
//   pending        not decided yet
export const SUBMISSION_LINK_STATES = ['pending', 'linked', 'manual', 'unverified', 'no_contact', 'not_connected', 'failed', 'skipped_spam'] as const
export type SubmissionLinkState = (typeof SUBMISSION_LINK_STATES)[number]

export const FORMS_SYSTEM_SUB = 'system:forms'

// ------------------------------------------------------- shapes over HTTP

export type FormSummary = {
  id: string
  name: string
  slug: string
  status: FormStatus
  publishedVersion: number | null
  opensAt: string | null
  closesAt: string | null
  submissionCap: number | null
  counts: { total: number; new: number }
  updatedAt: string
}

export type FormVersionSummary = { version: number; title: string; publishedAt: string; publishedByName: string | null; sha256: string }

export type FormDetail = FormSummary & {
  templateKey: string | null
  draft: FormDefinition
  draftUpdatedAt: string
  draftChanged: boolean
  published: FormDefinition | null
  versions: FormVersionSummary[]
  sendConfirmation: boolean
  retentionDays: number | null
  routing: FormRouting
  routingUpdatedByName: string | null
  settingsVersion: number
  createdAt: string
}

export type SubmissionSummary = {
  id: string
  formId: string
  formName: string
  formVersion: number
  status: SubmissionStatus
  spamReasons: string[]
  submittedAt: string
  contactName: string | null
  contactEmail: string | null
  organizationName: string | null
  personId: string | null
  personName: string | null
  linkState: SubmissionLinkState
  assigneePersonId: string | null
  assigneeName: string | null
  redactedAt: string | null
  fileCount: number
}

export type SubmissionFile = {
  id: string
  fieldKey: string
  filename: string | null
  contentType: string
  sizeBytes: number
  sha256: string
  status: 'quarantined' | 'accepted' | 'rejected' | 'purge_pending' | 'purged'
  documentId: string | null
  reviewedByName: string | null
  reviewedAt: string | null
}

export type SubmissionRoutingAction = {
  id: string
  action: 'support_case' | 'task' | 'checklist' | 'notify' | 'organization_proposal' | 'confirmation_email' | 'volunteer_application' | 'start_workflow' | 'board_prospect'
  status: 'done' | 'waiting' | 'skipped' | 'failed'
  targetType: string | null
  targetId: string | null
  detail: Record<string, unknown> | null
  updatedAt: string
}

export type SubmissionDetail = SubmissionSummary & {
  definition: FormDefinition
  // null once redacted. A sensitive field the viewer can't see is left out
  // and listed in hiddenFields.
  answers: FormAnswers | null
  hiddenFields: string[]
  contactPhone: string | null
  consents: { fieldKey: string; statement: string; granted: boolean; recordedAt: string }[]
  files: SubmissionFile[]
  routing: SubmissionRoutingAction[]
  emails: { kind: string; status: string; reason: string | null; updatedAt: string }[]
  organizationProposal: { id: string; proposedName: string | null; role: string; status: 'pending' | 'accepted' | 'dismissed'; organizationId: string | null; organizationName: string | null; relationshipId: string | null } | null
  notes: { id: string; body: string; authorName: string | null; createdAt: string }[]
  retentionHold: boolean
  redactedReason: string | null
  retentionEligibleOn: string | null
}

// ---------------------------------------------------------------- templates

type TemplateField = Omit<FormField, 'required' | 'sensitive'> & { required?: boolean; sensitive?: boolean }
const f = (field: TemplateField): FormField => ({ required: false, sensitive: false, ...field })
const contactFields = (): FormField[] => [
  f({ key: 'name', type: 'short_text', label: 'Your name', required: true, role: 'contact_name', maxLength: 200 }),
  f({ key: 'email', type: 'email', label: 'Email', required: true, role: 'contact_email' }),
  f({ key: 'phone', type: 'phone', label: 'Phone', role: 'contact_phone' }),
]
const marketing = () =>
  f({
    key: 'marketing_consent',
    type: 'checkbox',
    purpose: 'marketing_consent',
    label: 'Send me news and offers from Burlington Riverfront Entertainment by email. I can unsubscribe at any time.',
  })

// The provisional TCPA wording (core 0053's BRE statement; counsel reviews
// it). Never pre-checked, never required.
export const SMS_MARKETING_CONSENT_LABEL =
  'Text me news, events and offers from Burlington Riverfront Entertainment (BRE) at the number I gave. Recurring automated marketing texts; frequency varies. Consent is not a condition of purchase. Msg & data rates may apply. Reply STOP to opt out, HELP for help.'
const smsMarketing = () => f({ key: 'sms_marketing_consent', type: 'checkbox', purpose: 'sms_marketing_consent', label: SMS_MARKETING_CONSENT_LABEL })

export type FormTemplate = {
  key: string
  name: string
  description: string
  slug: string
  definition: FormDefinition
  routing: FormRouting
  // Not offered for new forms, and why. Forms already made from it keep
  // working.
  retired?: string
}

// Decision 3 (ADR 0041): Hub offers no rental or sponsorship intake of its own
// until Booking's commands exist.
export const RENTAL_INTAKE_RETIRED = 'Rental and sponsorship inquiries come in through the public inquiry and sponsor-opportunity pages.'

export const FORM_TEMPLATES: FormTemplate[] = [
  {
    key: 'volunteer',
    name: 'Volunteer sign-up',
    description: 'People offering to help at events: availability, interests and an emergency contact.',
    slug: 'volunteer',
    definition: {
      title: 'Volunteer with BRE',
      intro: 'Thanks for offering to help. Tell us how to reach you and when you’re free, and our volunteer coordinator will be in touch.',
      fields: [
        ...contactFields(),
        f({ key: 'interests', type: 'multiple_choice', label: 'What would you like to help with?', options: ['Gate and tickets', 'Hospitality', 'Setup and teardown', 'Information booth', 'Anywhere needed'], required: true }),
        f({ key: 'availability', type: 'long_text', label: 'When are you available?', help: 'Dates, days of the week or events.', maxLength: 1000 }),
        f({ key: 'age_18', type: 'checkbox', label: 'I am 18 or older, or I will volunteer with a parent or guardian.', required: true, purpose: 'acknowledgement' }),
        f({ key: 'emergency_contact', type: 'short_text', label: 'Emergency contact (name and phone)', sensitive: true, maxLength: 200 }),
        marketing(),
        smsMarketing(),
      ],
      confirmationMessage: 'Thanks for signing up to volunteer. Our coordinator will contact you before the next event.',
    },
    routing: { task: { priority: 'normal', dueInDays: 7 }, volunteerApplication: {} },
  },
  {
    key: 'vendor',
    name: 'Vendor application',
    description: 'Food, craft and merchandise vendors applying for a space, with their business details and certificate of insurance.',
    slug: 'vendor-application',
    definition: {
      title: 'Vendor application',
      intro: 'Apply for a vendor space at a BRE event. We review every application and reply within two weeks.',
      fields: [
        f({ key: 'business_name', type: 'short_text', label: 'Business name', required: true, role: 'organization_name', maxLength: 200 }),
        ...contactFields(),
        f({ key: 'vendor_type', type: 'single_choice', label: 'What do you sell?', options: ['Food', 'Beverages', 'Crafts', 'Merchandise', 'Services', 'Other'], required: true }),
        f({ key: 'description', type: 'long_text', label: 'Describe your products', required: true, maxLength: 2000 }),
        f({ key: 'events', type: 'long_text', label: 'Which events are you interested in?', maxLength: 1000 }),
        f({ key: 'insurance', type: 'file', label: 'Certificate of insurance', help: 'PDF or image, up to 10 MB.', accept: ['application/pdf', 'image/png', 'image/jpeg'], maxFiles: 1, maxSizeMb: 10 }),
        f({ key: 'tax_id', type: 'short_text', label: 'Iowa sales tax permit number', sensitive: true, maxLength: 60 }),
        f({ key: 'rules', type: 'checkbox', label: 'I have read and agree to follow the BRE vendor rules.', required: true, purpose: 'acknowledgement' }),
        marketing(),
        smsMarketing(),
      ],
      confirmationMessage: 'Thanks for applying. We review vendor applications every week and will reply by email.',
    },
    routing: { organizationProposal: { role: 'vendor' }, supportCase: { queue: 'general', priority: 'normal' } },
  },
  {
    key: 'rental',
    name: 'Rental inquiry',
    description: 'Someone interested in renting a BRE space: date, guest count and the kind of event.',
    slug: 'rental-inquiry',
    retired: RENTAL_INTAKE_RETIRED,
    definition: {
      title: 'Rental inquiry',
      intro: 'Tell us about your event and we’ll follow up with availability and pricing.',
      fields: [
        ...contactFields(),
        f({ key: 'organization', type: 'short_text', label: 'Organization (if any)', role: 'organization_name', maxLength: 200 }),
        f({ key: 'event_type', type: 'single_choice', label: 'Type of event', options: ['Wedding or reception', 'Corporate', 'Fundraiser', 'Concert or show', 'Private party', 'Other'], required: true }),
        f({ key: 'preferred_date', type: 'date', label: 'Preferred date' }),
        f({ key: 'guests', type: 'number', label: 'Expected guests', min: 1, max: 20000, wholeNumber: true }),
        f({ key: 'details', type: 'long_text', label: 'Anything else we should know?', maxLength: 2000 }),
        marketing(),
        smsMarketing(),
      ],
      confirmationMessage: 'Thanks for your inquiry. Our rentals team will contact you within two business days.',
    },
    routing: { supportCase: { queue: 'rentals', priority: 'normal' } },
  },
  {
    key: 'sponsorship',
    name: 'Sponsorship inquiry',
    description: 'Businesses interested in sponsoring events or venues.',
    slug: 'sponsorship-inquiry',
    retired: RENTAL_INTAKE_RETIRED,
    definition: {
      title: 'Sponsorship inquiry',
      intro: 'Interested in sponsoring an event or venue? Tell us about your organization and our partnerships team will reach out.',
      fields: [
        f({ key: 'company', type: 'short_text', label: 'Company or organization', required: true, role: 'organization_name', maxLength: 200 }),
        ...contactFields(),
        f({ key: 'interest', type: 'multiple_choice', label: 'What interests you?', options: ['Event sponsorship', 'Venue naming', 'Hospitality packages', 'In-kind partnership', 'Not sure yet'] }),
        f({ key: 'budget', type: 'single_choice', label: 'Approximate budget', options: ['Under $2,500', '$2,500 to $10,000', '$10,000 to $50,000', 'Over $50,000', 'Prefer not to say'], sensitive: true }),
        f({ key: 'message', type: 'long_text', label: 'Tell us more', maxLength: 2000 }),
        marketing(),
        smsMarketing(),
      ],
      confirmationMessage: 'Thanks for your interest in partnering with BRE. Our partnerships team will contact you soon.',
    },
    routing: { organizationProposal: { role: 'sponsor' }, supportCase: { queue: 'sponsorship', priority: 'normal' } },
  },
  {
    key: 'contact',
    name: 'General contact',
    description: 'A plain question or message for BRE staff.',
    slug: 'contact',
    definition: {
      title: 'Contact BRE',
      intro: 'Send us a question or a message and we’ll get back to you.',
      fields: [
        ...contactFields(),
        f({ key: 'topic', type: 'single_choice', label: 'Topic', options: ['Tickets', 'Events', 'Rentals', 'Rewards', 'Something else'], required: true }),
        f({ key: 'message', type: 'long_text', label: 'Message', required: true, maxLength: 4000 }),
      ],
      confirmationMessage: 'Thanks for your message. We usually reply within two business days.',
    },
    routing: { supportCase: { queue: 'general', priority: 'normal' } },
  },
  {
    key: 'feedback',
    name: 'Event feedback',
    description: 'How an event went, from someone who was there.',
    slug: 'event-feedback',
    definition: {
      title: 'Event feedback',
      intro: 'Thanks for coming. Tell us how it went; it helps us plan the next one.',
      fields: [
        f({ key: 'event', type: 'short_text', label: 'Which event did you attend?', required: true, maxLength: 200 }),
        f({ key: 'rating', type: 'single_choice', label: 'Overall, how was it?', options: ['Excellent', 'Good', 'Okay', 'Poor'], required: true }),
        f({ key: 'liked', type: 'long_text', label: 'What did you enjoy?', maxLength: 2000 }),
        f({ key: 'improve', type: 'long_text', label: 'What could be better?', maxLength: 2000 }),
        f({ key: 'contact_section', type: 'section', label: 'Want a reply?', help: 'Leave your email only if you’d like us to follow up.' }),
        f({ key: 'name', type: 'short_text', label: 'Your name', role: 'contact_name', maxLength: 200 }),
        f({ key: 'email', type: 'email', label: 'Email', role: 'contact_email' }),
        marketing(),
      ],
      confirmationMessage: 'Thanks for your feedback.',
    },
    routing: {},
  },
  // The public interest form (ADR 0060, owner decision 4): each submission
  // becomes a prospect for the Nominating Committee.
  {
    key: 'board_interest',
    name: 'Board interest',
    description: 'People interested in serving on the BRE Board of Directors. Each submission becomes a prospect for the Nominating Committee.',
    slug: 'board-interest',
    definition: {
      title: 'Interested in serving on the board?',
      intro: 'Burlington Riverfront Entertainment’s Board of Directors is elected each June at the Annual Meeting, for three-year terms beginning July 1. Tell us a little about yourself and the Nominating Committee will be in touch.',
      fields: [
        ...contactFields(),
        f({ key: 'city', type: 'short_text', label: 'City and state', maxLength: 120 }),
        f({ key: 'why', type: 'long_text', label: 'Why are you interested in serving on the board?', required: true, maxLength: 3000 }),
        f({ key: 'experience', type: 'long_text', label: 'What experience, skills or connections would you bring?', maxLength: 3000 }),
        f({
          key: 'areas',
          type: 'multiple_choice',
          label: 'Which areas interest you most?',
          options: ['Finance and audit', 'Fundraising and sponsorships', 'Events and programming', 'Marketing and community relations', 'Facilities and operations', 'Governance and policy', 'Not sure yet'],
        }),
        f({ key: 'heard', type: 'short_text', label: 'How did you hear about serving on the board?', maxLength: 300 }),
        f({ key: 'understood', type: 'checkbox', label: 'I understand that the Nominating Committee reviews every expression of interest and may contact me, and that this form is not a nomination.', required: true, purpose: 'acknowledgement' }),
      ],
      confirmationMessage: 'Thank you for your interest in serving on the BRE Board of Directors. The Nominating Committee reviews every expression of interest and will be in touch.',
    },
    routing: { boardProspect: {} },
  },
]

export const findFormTemplate = (key: string) => FORM_TEMPLATES.find((t) => t.key === key) ?? null
// The templates offered for a new form.
export const OFFERED_FORM_TEMPLATES = FORM_TEMPLATES.filter((t) => !t.retired)

// Slugs are the public path on the forms host: lowercase words and
// hyphens. A few are reserved for the forms host itself.
export const FORM_SLUG = /^[a-z0-9](?:[a-z0-9-]{1,58}[a-z0-9])$/
export const RESERVED_FORM_SLUGS = ['api', 'auth', 'assets', 'static', 'forms', 'health', 'robots-txt', 'favicon', 'admin', 'hub', 'login', 'records']
export const isValidFormSlug = (slug: string) => FORM_SLUG.test(slug) && !slug.includes('--') && !RESERVED_FORM_SLUGS.includes(slug)

// Spam protection defaults (docs/runbooks/deploy.md, "Forms").
export const FORM_MIN_FILL_MS = 3000
export const FORM_TOKEN_MAX_AGE_MS = 24 * 60 * 60 * 1000
export const FORM_RATE_LIMITS = {
  // One IP address, across every form.
  ipPerTenMinutes: 5,
  ipPerDay: 30,
  // One form, from everyone.
  formPerHour: 120,
  // Confirmation emails to one address, across every form.
  confirmationsPerAddressPerDay: 3,
} as const
